import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import { useAccountBookStore } from '@/store/accountBookStore';
import SectionHeader from '../SectionHeader';

interface QuarterlyData {
  revenue: number;
  expense: number;
  profit: number;
  profitMargin: number;
}

const QuarterlyAnalysisSection: React.FC = () => {
  const theme = useTheme();
  const { dashboardData } = useAccountBookStore();

  const formatNumber = (num: number): string => {
    return num.toLocaleString('ko-KR');
  };

  const calculateProfitMargin = (revenue: number, expense: number): number => {
    if (revenue === 0) return 0;
    return Math.round(((revenue - expense) / revenue) * 100 * 100) / 100;
  };

  const quarterlyData = useMemo(() => {
    const quarters: { [key: string]: QuarterlyData } = {
      Q1: { revenue: 0, expense: 0, profit: 0, profitMargin: 0 },
      Q2: { revenue: 0, expense: 0, profit: 0, profitMargin: 0 },
      Q3: { revenue: 0, expense: 0, profit: 0, profitMargin: 0 },
      Q4: { revenue: 0, expense: 0, profit: 0, profitMargin: 0 },
    };

    dashboardData.monthlyData.forEach(month => {
      const quarter = Math.floor((month.month - 1) / 3) + 1;
      const quarterKey = `Q${quarter}` as keyof typeof quarters;
      quarters[quarterKey].revenue += month.income;
      quarters[quarterKey].expense += month.expense;
      quarters[quarterKey].profit += month.netIncome;
    });

    // 영업이익률 계산
    Object.keys(quarters).forEach(key => {
      const q = quarters[key as keyof typeof quarters];
      q.profitMargin = calculateProfitMargin(q.revenue, q.expense);
    });

    return quarters;
  }, [dashboardData.monthlyData]);

  const quarters = [
    { key: 'Q1', label: '1분기 (1-3월)', months: '1-3월' },
    { key: 'Q2', label: '2분기 (4-6월)', months: '4-6월' },
    { key: 'Q3', label: '3분기 (7-9월)', months: '7-9월' },
    { key: 'Q4', label: '4분기 (10-12월)', months: '10-12월' },
  ];

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: { xs: 2, sm: 3 },
        background: 'var(--bg-base)',
        borderRadius: 'var(--r-16)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <SectionHeader
        eyebrow="분기 추이"
        title="분기별 분석"
        subtitle={`${dashboardData.selectedYear}년 분기별 매출 · 지출 · 영업이익`}
      />

      <Grid container spacing={2}>
        {quarters.map((quarter) => {
          const data = quarterlyData[quarter.key as keyof typeof quarterlyData];
          return (
            <Grid item xs={12} sm={6} key={quarter.key}>
              <Card
                sx={{
                  height: '100%',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--r-12)',
                  transition: 'all var(--motion-base)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 'var(--shadow-2)',
                  },
                }}
              >
                <CardContent>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 2,
                      color: 'primary.main'
                    }}
                  >
                    {quarter.label}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        매출
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {formatNumber(data.revenue)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        지출
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {formatNumber(data.expense)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        영업이익
                      </Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: 'text.primary'
                        }}
                      >
                        {formatNumber(data.profit)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        영업이익률
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 600,
                          color: 'text.primary'
                        }}
                      >
                        {data.profitMargin >= 0 ? '+' : ''}{data.profitMargin.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default QuarterlyAnalysisSection;

